import type {
  DateInputString,
  HourAndMinuteString,
  Nullable,
} from '#lit/types/general.js';
import type {Timezone} from '#lit/types/timezone.js';
import {compare} from '#lit/utils/objects.js';
import {isDateInputString, isHourAndMinuteString} from '#lit/utils/dates.js';
import {isString} from '#lit/utils/index.js';
import {isKnownTimezone} from '#lit/common/constants/timezones.js';
import type FlyScheduleDateInput from './index.js';

export type ScheduleData = {
  scheduled: boolean;
  date: DateInputString | '';
  time: HourAndMinuteString | '';
  timezone: Nullable<Timezone>;
};

const notScheduled = compare<ScheduleData>({
  scheduled: false,
  date: '',
  time: '',
  timezone: null,
});

const scheduled = compare<ScheduleData>({
  scheduled: true,
  date: isDateInputString,
  time: isHourAndMinuteString,
  timezone: value => isString(value) && isKnownTimezone(value),
});

export function validateSchedule(scheduleData: ScheduleData): boolean {
  return notScheduled(scheduleData) || scheduled(scheduleData);
}

export function dispatchEvents(
  host: FlyScheduleDateInput,
  changedProperty: 'scheduled' | 'date' | 'time' | 'timezone'
): void {
  if (!host.isConnected) return;
  const {scheduled, date, time, timezone, valid} = host.model;

  host.dispatchEvent(
    new CustomEvent(`scheduledate:change:${changedProperty}`, {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: {
        value: host.model[changedProperty],
      },
    })
  );

  host.dispatchEvent(
    new CustomEvent('scheduledate:change', {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: {
        scheduled,
        date,
        time,
        timezone,
        valid,
      },
    })
  );

  if (!valid) {
    host.dispatchEvent(
      new CustomEvent('scheduledate:change:valid', {
        bubbles: true,
        cancelable: false,
        composed: true,
        detail: {
          scheduled,
          date,
          time,
          timezone,
        },
      })
    );
  }
}
